// Phase 18 · Community · Admin moderation queue
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shield, EyeOff, X as XIcon, Ban, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = [
    { value: "hide", label: "Hide content", icon: <EyeOff size={13} />, color: "text-neutral-400" },
    { value: "dismiss", label: "Dismiss (no action)", icon: <Check size={13} />, color: "text-neutral-400" },
    { value: "ban_user", label: "Ban author", icon: <Ban size={13} />, color: "text-neutral-400" },
];

export default function AdminCommunityModeration() {
    const [statusFilter, setStatusFilter] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [disabled, setDisabled] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setDisabled(false);
        try {
            const r = await fetch(`${API}/api/admin/community/moderation/queue?status=${statusFilter}&limit=100`, {
                credentials: "include",
            });
            if (r.status === 503) { setDisabled(true); setItems([]); return; }
            if (r.status === 403) { toast.error("Admin only"); return; }
            if (!r.ok) throw new Error();
            const d = await r.json();
            setItems(d.items || []);
        } catch { setItems([]); }
        setLoading(false);
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const resolve = async (reportId, action) => {
        setBusyId(reportId);
        try {
            const r = await fetch(`${API}/api/admin/community/moderation/${reportId}/resolve`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ action, notes: "" }),
            });
            if (!r.ok) {
                toast.error("Resolve failed");
                return;
            }
            toast.success(`Report resolved: ${action}`);
            load();
        } catch { toast.error("Network error"); }
        finally { setBusyId(null); }
    };

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter"
            data-testid="admin-moderation-page"
        >
            <header className="border-b border-[#1A1A1A]">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Shield size={22} className="text-[#FAFAFA]" />
                        <div>
                            <h1 className="text-lg font-semibold">Community Moderation</h1>
                            <p className="text-xs text-neutral-500">Reactive queue · Phase 18</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        {["pending", "resolved_hide", "resolved_dismiss", "resolved_ban", "all"].map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-1.5 rounded-full border transition-all ${statusFilter === s ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                                data-testid={`mod-filter-${s}`}
                            >
                                {s.replace("resolved_", "")}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6">
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA] mb-6">
                        Community engine disabled — enable in System Settings to see the queue.
                    </div>
                )}
                {loading && <p className="text-sm text-neutral-500">Loading…</p>}
                {!loading && !items.length && !disabled && (
                    <p className="text-sm text-neutral-500 italic">No reports at this status</p>
                )}
                <div className="space-y-3">
                    {items.map((r) => (
                        <div
                            key={r.id}
                            className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4"
                            data-testid={`mod-report-${r.id}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                            r.status === "pending" ? "bg-neutral-500/10 text-neutral-400" :
                                            r.status.startsWith("resolved_hide") ? "bg-neutral-700/20 text-neutral-300" :
                                            r.status.startsWith("resolved_dismiss") ? "bg-neutral-700/20 text-neutral-400" :
                                            r.status.startsWith("resolved_ban") ? "bg-neutral-500/10 text-neutral-400" :
                                            "bg-neutral-700/20 text-neutral-400"
                                        }`}>
                                            {r.status}
                                        </span>
                                        <span className="text-xs text-neutral-500">
                                            {r.target_type} · reason: <b>{r.reason}</b>
                                        </span>
                                        <span className="text-xs text-neutral-500">·</span>
                                        <span className="text-xs text-neutral-500">
                                            {r.created_at?.slice(0, 19).replace("T", " ")}
                                        </span>
                                    </div>
                                    {r.target_snapshot ? (
                                        <div>
                                            {r.target_type === "post" && (
                                                <Link
                                                    to={`/community/post/${r.target_snapshot.id}`}
                                                    className="text-sm text-neutral-200 hover:text-[#FFFFFF] font-medium inline-flex items-center gap-1"
                                                >
                                                    {r.target_snapshot.title}
                                                    <ExternalLink size={12} />
                                                </Link>
                                            )}
                                            {r.target_type === "comment" && (
                                                <p className="text-sm text-neutral-200 line-clamp-2">
                                                    "{r.target_snapshot.body}"
                                                </p>
                                            )}
                                            <p className="text-xs text-neutral-500 mt-1">
                                                by @{r.target_snapshot.author_username} · target status: <b>{r.target_snapshot.status}</b>
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-neutral-600 italic">Target deleted</p>
                                    )}
                                    {r.notes && (
                                        <p className="mt-2 text-xs text-neutral-400 border-l-2 border-[#1A1A1A] pl-2">
                                            {r.notes}
                                        </p>
                                    )}
                                </div>
                                {r.status === "pending" && (
                                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                                        {ACTIONS.map((a) => (
                                            <button
                                                key={a.value}
                                                type="button"
                                                onClick={() => resolve(r.id, a.value)}
                                                disabled={busyId === r.id}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1 border border-[#1A1A1A] rounded text-xs hover:bg-white/5 transition-all ${a.color} disabled:opacity-40`}
                                                data-testid={`mod-action-${a.value}-${r.id}`}
                                            >
                                                {a.icon} {a.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
}
